import React, { useMemo } from 'react'
import { Menu } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { classNames } from 'util/classNames'

interface DropdownOption {
  id: string
  label: string
  disabled?: boolean
}

interface DropdownProps {
  label?: string
  labelPosition?: 'above' | 'inside'
  options: DropdownOption[]
  className?: string
  labelClassName?: string
  description?: string
  icon?: React.ReactNode
  variant?: 'solid' | 'outline' | 'text' | 'warning'
  color?: 'slate' | 'blue' | 'white' | 'red' | 'yellow'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  value?: string
  onChange?: (value: string) => void
}

export const Dropdown: React.FC<DropdownProps> = ({
  label,
  labelPosition = 'above',
  options,
  className = '',
  icon,
  disabled = false,
  labelClassName = '',
  description,
  variant = 'solid',
  color = 'blue',
  size = 'md',
  value,
  onChange,
}) => {
  const selectedOption = options.find((o) => o.id === value)

  const getBaseSize = () => {
    switch (size) {
      case 'xs':
        return 'py-[4px] px-2 text-[11px]'
      case 'sm':
        return 'py-[6px] px-2 text-[12px]'
      case 'md':
        return 'py-[9px] px-3 text-[13px]'
      case 'lg':
        return 'py-[10px] px-4 text-sm'
      case 'xl':
        return 'py-4 px-4 text-base'
      default:
        return 'py-[9px] px-3 text-[13px]'
    }
  }

  const getBaseColor = () => {
    if (disabled) {
      return 'bg-gray-300 text-gray-500 border-gray-300'
    }

    if (variant === 'outline') {
      if (color === 'blue') {
        return `bg-transparent border border-toasty-blue-600 text-toasty-blue-600`
      }
      return `bg-transparent border border-${color}-400 text-${color}-500`
    }

    if (variant === 'warning') {
      return `bg-slate-500 hover:bg-red-600 text-white border border-slate-500`
    }

    if (variant === 'text') {
      return `bg-transparent text-${color}-500 hover:bg-${color}-100 border border-transparent`
    }

    switch (color) {
      case 'slate':
        return 'bg-slate-500 text-white hover:bg-slate-600 border border-slate-500'
      case 'blue':
        return 'bg-toasty-blue-500 hover:bg-toasty-blue-600 text-white border border-toasty-blue-500'
      case 'white':
        return 'bg-white border border-gray-200 hover:bg-gray-100 text-gray-800'
      case 'red':
        return 'bg-red-500 text-red-100 border border-red-500'
      case 'yellow':
        return 'bg-yellow-500 hover:bg-yellow-600 text-white border border-yellow-500'
      default:
        return 'bg-toasty-blue-600 hover:bg-toasty-blue-500 text-white border border-toasty-blue-500'
    }
  }

  const buttonClass = useMemo(() => {
    const baseSize = getBaseSize()
    const baseColor = getBaseColor()

    return classNames(
      'inline-flex items-center max-w-md justify-center rounded-md font-semibold focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 transition-all duration-300 ease-in-out',
      baseSize,
      baseColor,
      disabled && 'opacity-50 cursor-not-allowed',
      className
    )
  }, [size, variant, color, disabled, className])

  const menuItemClass = ({
    active,
    disabled,
  }: {
    active: boolean
    disabled: boolean
  }) =>
    classNames(
      'block w-full max-w-md px-4 py-2 text-left text-sm',
      disabled && 'opacity-50 cursor-not-allowed',
      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
    )

  const displayLabel =
    selectedOption?.label ??
    (labelPosition === 'inside' && label ? label : 'Select')

  return (
    <div className="relative inline-block w-full max-w-md text-left">
      {labelPosition === 'above' && label && (
        <label className={classNames('mb-1 flex-col gap-y-1', labelClassName)}>
          <p className="text-sm">{label}</p>
          {description && (
            <p className="text-xs text-gray-500">{description}</p>
          )}
        </label>
      )}
      <Menu>
        <Menu.Button className="w-full max-w-md text-left" disabled={disabled}>
          <div
            className={classNames(
              'flex items-center space-x-2',
              buttonClass,
              labelPosition === 'above' && 'mt-2'
            )}
          >
            {icon && <span>{icon}</span>}
            <span>{displayLabel}</span>
            <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
          </div>
        </Menu.Button>
        <Menu.Items className="absolute bottom-[35px] left-0 z-10 mb-2 mt-2 max-h-[200px] w-full max-w-md origin-top-left overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none md:bottom-auto md:mb-0 md:mt-2">
          <div className="py-1">
            {options.map((option) => (
              <Menu.Item key={option.id} disabled={option.disabled}>
                {({ active, disabled }) => (
                  <button
                    onClick={() => {
                      if (onChange && !disabled) onChange(option.id)
                    }}
                    className={menuItemClass({ active, disabled })}
                  >
                    {option.label}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Menu>
    </div>
  )
}
